import { Bell, CheckCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "../contexts/NotificationContext.jsx";

const NotificationBell = ({ compact = false }) => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const {
    isAuthenticated,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
  } = useNotificationContext();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
  }, [open]);

  if (!isAuthenticated) {
    return null;
  }

  const getNotificationTarget = (notification) => {
    // A DealSwapify system notice always opens its dedicated notice page.
    // Do this before generic actionUrl handling so in-app notice navigation
    // cannot accidentally diverge from push-notification navigation.
    if (
      notification?.referenceType === "SYSTEM_NOTICE" &&
      notification?.referenceId
    ) {
      return `/notices/${encodeURIComponent(
        notification.referenceId
      )}`;
    }

    if (notification?.actionUrl) {
      return notification.actionUrl;
    }

    if (
      notification?.referenceType === "CHAT_ROOM" &&
      notification?.referenceId
    ) {
      return `/inbox?roomId=${encodeURIComponent(
        notification.referenceId
      )}`;
    }

    if (
      notification?.referenceType === "ITEM" &&
      notification?.referenceId
    ) {
      return `/item/${encodeURIComponent(
        notification.referenceId
      )}`;
    }

    return null;
  };

  const openNotification = async (notification) => {
    if (!notification.read) {
      await markAsRead(
        notification.notificationId
      );
    }

    setOpen(false);

    const target = getNotificationTarget(notification);

    if (target) {
      navigate(target);
    }
  };

  const formatTime = (createdAt) => {
    if (!createdAt) return "";

    const time = new Date(createdAt).getTime();

    if (!Number.isFinite(time)) return "";

    const difference = Date.now() - time;
    const minutes = Math.floor(
      difference / 60000
    );
    const hours = Math.floor(
      difference / 3600000
    );
    const days = Math.floor(
      difference / 86400000
    );

    if (minutes < 1) return "now";
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;

    return new Date(time).toLocaleDateString();
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={
          unreadCount
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        aria-expanded={open}
        onClick={() =>
          setOpen((current) => !current)
        }
        className={`relative flex items-center justify-center rounded-lg border border-border bg-secondary text-secondary-foreground transition-colors hover:bg-muted ${
          compact
            ? "h-9 w-9"
            : "h-10 w-10"
        }`}
      >
        <Bell className="h-4 w-4" />

        {unreadCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground">
            {unreadCount > 99
              ? "99+"
              : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-[80] mt-2 w-[min(86vw,350px)] overflow-hidden rounded-xl border border-border bg-card shadow-xl sm:w-[380px]">
          <div className="flex items-center justify-between border-b border-border px-3 py-2 sm:px-4 sm:py-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Notifications
              </h3>

              <p className="text-xs text-muted-foreground">
                {unreadCount === 0
                  ? "You're all caught up"
                  : `${unreadCount} unread`}
              </p>
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[220px] overflow-y-auto sm:max-h-[420px]">
            {notifications.length === 0 ? (
              <div className="px-4 py-7 text-center sm:py-10">
                <Bell className="mx-auto mb-2 h-7 w-7 text-muted-foreground/60" />

                <p className="text-sm font-medium text-foreground">
                  No notifications yet
                </p>

                <p className="mt-1 text-xs text-muted-foreground">
                  New messages and marketplace updates will appear here.
                </p>
              </div>
            ) : (
              notifications.map(
                (notification) => (
                  <button
                    key={notification.notificationId}
                    type="button"
                    onClick={() =>
                      openNotification(notification)
                    }
                    className={`flex w-full gap-3 border-b border-border/60 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted sm:px-4 sm:py-3 ${
                      notification.read
                        ? "bg-card"
                        : "bg-primary/5"
                    }`}
                  >
                    {notification.imageUrl ? (
                      <img
                        src={notification.imageUrl}
                        alt=""
                        className="h-9 w-9 shrink-0 rounded-full object-cover sm:h-10 sm:w-10"
                      />
                    ) : (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary sm:h-10 sm:w-10">
                        <Bell className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <p
                          className={`min-w-0 flex-1 truncate text-sm ${
                            notification.read
                              ? "font-medium"
                              : "font-semibold"
                          } text-foreground`}
                        >
                          {notification.title ||
                            "Notification"}
                        </p>

                        {notification.type === "NEW_MESSAGE" &&
                          Number(notification.occurrenceCount || 1) > 1 && (
                            <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                              {notification.occurrenceCount} new
                            </span>
                          )}

                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {formatTime(
                            notification.createdAt
                          )}
                        </span>
                      </div>

                      {notification.message && (
                        <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-muted-foreground sm:leading-5">
                          {notification.message}
                        </p>
                      )}
                    </div>

                    {!notification.read && (
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    )}
                  </button>
                )
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;